/**
 * Asset management commands — search, add, remove, list, info, create, doctor.
 * All delegate to @skaile/workspaces/asset-manager's AssetManager class.
 */

import path from "node:path";
import * as p from "@clack/prompts";
import {
  AssetManager,
  type BackendDeclaredAsset,
  type DeclaredAsset,
  type DeclaredState,
  MissingManifestError,
  NON_DEPLOYABLE_KINDS,
  parseRefTolerant,
} from "@skaile/workspaces/asset-manager";
import {
  ASSET_KINDS,
  type CatalogEntry,
  type DriverTarget,
  factoryAssetRoots,
  parseAssetRef,
  scanDirectory,
  SUPPORTED_DRIVER_TARGETS,
} from "@skaile/workspaces/core";
import type { CatalogAsset } from "@skaile/workspaces/plugins";
import { Command, Option } from "commander";
import pc from "picocolors";
import {
  bareSoftDepName,
  colorRef,
  formatSoftDepHint,
  formatSoftDepSection,
  kindColor,
  kindColorPad,
  logErr,
  logInfo,
  logOk,
  S,
} from "../helpers.ts";

/**
 * Copyable install ref for a store catalog asset — `kind:@<publisher>/<name>#<version>`,
 * the exact shape `skaile add` accepts.
 */
export function catalogAssetRef(a: CatalogAsset): string {
  return `${a.kind}:@${a.publisher}/${a.name}#${a.version}`;
}

/**
 * Copyable ref for a local index entry. Uses `kind:@<publisher>/<name>` when a
 * publisher is known; local assets without one fall back to the bare name.
 */
export function localEntryRef(e: Pick<CatalogEntry, "kind" | "name" | "publisher">): string {
  return e.publisher ? `${e.kind}:@${e.publisher}/${e.name}` : e.name;
}

/**
 * Client-side text filter over catalog assets — the store's `listAssets` has no
 * server-side query. Case-insensitive match across name, description, publisher;
 * an empty query returns every asset.
 */
export function filterCatalogAssets(
  assets: CatalogAsset[],
  query: string | undefined,
): CatalogAsset[] {
  if (!query) return assets;
  const q = query.toLowerCase();
  return assets.filter(
    (a) =>
      a.name.toLowerCase().includes(q) ||
      (a.description?.toLowerCase().includes(q) ?? false) ||
      a.publisher.toLowerCase().includes(q),
  );
}

/**
 * Drive the `skaile search` action across the local and/or store legs.
 * Air-gapped (`cache_ttl: 0`) suppresses every network leg. The store leg goes
 * through the shared catalog client (same source `install`/`update` use).
 */
async function runSearch(
  kind: string | undefined,
  query: string | undefined,
  opts: any,
): Promise<void> {
  if (kind?.includes(":")) {
    query = kind;
    kind = undefined;
  }

  const showLocal = !opts.store;
  const showStore = !opts.local;

  const { resolveConfig } = await import("@skaile/workspaces/library");
  const airGapped =
    resolveConfig({ projectDir: path.resolve(opts.projectDir) }).catalog.cache_ttl === 0;

  if (showLocal) {
    await searchLocal(query, kind, opts, showStore, airGapped);
  }

  if (showStore) {
    await searchStore(query, kind, opts, airGapped);
  }
}

/**
 * Run the local-repo search and print its grouped table (or empty notice).
 *
 * On an empty result that is *not* a query miss (no matches for nothing → never
 * synced), auto-sync the project's sources and re-query, unless air-gapped
 * (which forbids the network). If auto-sync doesn't surface anything, fall back
 * to the "run sync first" hint.
 */
/**
 * Read local assets straight from the index (`asset_definitions`) rather than
 * re-scanning the source tree. A `source add`/`sync` is authoritative for what
 * it wrote, so reading the index returns the exact set it holds — no
 * hand-rolled filename walk, no `kind:name` dedup collapsing same-named or
 * multi-kind assets. Maps each `AssetDefinition` row → the `CatalogEntry` shape
 * `printLocalEntries` / `localEntryRef` consume.
 */
async function searchIndex(
  query: string | undefined,
  kind: string | undefined,
): Promise<CatalogEntry[]> {
  const { openLibrary } = await import("../open-library.ts");
  const library = await openLibrary();
  try {
    const defs = await library.listAssetDefs(kind ? { kind: kind as CatalogEntry["kind"] } : {});
    let entries: CatalogEntry[] = defs.map(
      (d): CatalogEntry => ({
        name: d.name,
        kind: d.kind as CatalogEntry["kind"],
        description: d.description ?? "",
        source: d.id,
        // Stored publishers may carry the `@` scope sigil (manifest-mode rows);
        // `localEntryRef` re-adds it, so strip here to avoid `@@`.
        publisher: d.publisher ? d.publisher.replace(/^@/, "") : undefined,
        version: d.version,
        requires: [],
        dependencies: [],
        license: d.license,
        category: d.category,
      }),
    );
    if (query) {
      const q = query.toLowerCase();
      entries = entries.filter(
        (e) => e.name.toLowerCase().includes(q) || e.description.toLowerCase().includes(q),
      );
    }
    return entries;
  } finally {
    library.close();
  }
}

/**
 * Scan the bundled FACTORY (first-party) source — it is never indexed, so the
 * index read alone would drop built-in skills from a fresh project's
 * `search --local`. Reuses `AssetManager.search` (which injects the factory
 * source into its repositories map) and keeps only entries whose manifest path
 * lives under a `factoryAssetRoots()` prefix — the same test `asset-feeds.ts`
 * uses to band the factory origin.
 */
async function searchFactory(
  projectDir: string,
  query: string | undefined,
  kind: string | undefined,
): Promise<CatalogEntry[]> {
  const prefixes = factoryAssetRoots().map((r) => (r.endsWith(path.sep) ? r : r + path.sep));
  const isFactoryPath = (p: string) =>
    prefixes.some((fp) => p === fp.slice(0, -1) || p.startsWith(fp));
  try {
    const entries = await new AssetManager({ projectDir }).search(query, kind);
    return entries.filter((e) => isFactoryPath(e.source));
  } catch {
    return [];
  }
}

/** Canonical dedup key for a local entry (`kind:@publisher/name`). */
function entryDedupKey(e: CatalogEntry): string {
  return `${e.kind}:${e.publisher ? `@${e.publisher}/` : ""}${e.name}`;
}

/** Union index entries with factory entries, index-wins on a canonical collision. */
function unionEntries(indexed: CatalogEntry[], factory: CatalogEntry[]): CatalogEntry[] {
  const seen = new Set(indexed.map(entryDedupKey));
  return [...indexed, ...factory.filter((e) => !seen.has(entryDedupKey(e)))];
}

async function searchLocal(
  query: string | undefined,
  kind: string | undefined,
  opts: any,
  showStore: boolean,
  airGapped: boolean,
): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  // Index (registered sources, overlay-applied) UNION the bundled factory source
  // (never indexed). Without the factory leg a fresh project would show no
  // built-in skills; without the index leg the count/dedup bug (Bug 3) returns.
  const factory = await searchFactory(projectDir, query, kind);
  let entries = unionEntries(await searchIndex(query, kind), factory);

  // Empty with no query *and* no kind filter = nothing indexed yet (a query or
  // kind miss is a real "no match", not an unsynced state). Auto-sync then
  // re-query against the index — never over the network when air-gapped.
  if (entries.length === 0 && !query && !kind && !airGapped) {
    const { autoSyncForSearch } = await import("./source.ts");
    if (await autoSyncForSearch(projectDir)) {
      entries = unionEntries(await searchIndex(query, kind), factory);
    }
  }

  if (entries.length === 0) {
    if (!showStore) {
      logInfo(
        query
          ? `No entries matching "${query}".`
          : kind
            ? `No "${kind}" assets found in this project's sources.`
            : "No assets found. Run `skaile source sync` first.",
      );
    }
    return;
  }

  if (showStore) {
    console.log(`\n  ${S.heading("Local:")}`);
  }
  printLocalEntries(entries);
}

/** Group local entries by kind and print one compact table per kind. */
function printLocalEntries(entries: CatalogEntry[]): void {
  const byKind = new Map<string, typeof entries>();
  for (const e of entries) {
    if (!byKind.has(e.kind)) byKind.set(e.kind, []);
    byKind.get(e.kind)!.push(e);
  }
  for (const [k, items] of byKind) {
    const nameW = Math.max(4, ...items.map((e) => localEntryRef(e).length));
    console.log();
    console.log(
      `  ${kindColor(k)} ${S.heading(`${k.toUpperCase()}S`)} ${S.dim(`(${items.length})`)}`,
    );
    console.log(`  ${S.rule(nameW + 62)}`);
    for (const e of items) {
      const ver = e.version ? S.dim(` v${e.version}`) : "";
      console.log(
        `  ${S.cmd(localEntryRef(e).padEnd(nameW))}  ${e.description.slice(0, 60)}${ver}`,
      );
    }
  }
  console.log(`\n  ${S.dim(`${entries.length} total`)}\n`);
}

/**
 * Query the remote store and print its results. Air-gapped skips the network;
 * a store failure degrades non-silently — `--store` surfaces the error, an
 * implicit query keeps local results behind a one-line notice.
 */
async function searchStore(
  query: string | undefined,
  kind: string | undefined,
  opts: any,
  airGapped: boolean,
): Promise<void> {
  if (airGapped) {
    if (opts.store) {
      logErr(
        "air-gapped (catalog.cache_ttl: 0) — store search is disabled. Set catalog.cache_ttl > 0 to query the store.",
      );
      process.exit(1);
    }
    logInfo("air-gapped (cache_ttl: 0) — local sources only");
    return;
  }
  // Imported before the try so the catch can `instanceof`-check the typed error.
  const { openCatalogSource, LocalCatalogDisabledError } = await import("../open-library.ts");
  try {
    const remote = await openCatalogSource({ projectDir: path.resolve(opts.projectDir) });
    // The store filters by kind server-side; text query is client-side only.
    const assets = await remote.listAssets(kind ? { kind } : {});
    const results = filterCatalogAssets(assets, query);

    if (results.length > 0) {
      console.log(`\n  ${S.heading("Store:")}`);
      for (const asset of results) {
        const ref = catalogAssetRef(asset);
        console.log(
          `    ⬡ ${kindColorPad(asset.kind)}  ${colorRef(ref).padEnd(48)}  ${S.dim(asset.description?.slice(0, 50) || "")}`,
        );
      }
    } else if (opts.store) {
      logInfo("No results in store");
    }
  } catch (e: any) {
    // `catalog.url: local` disables the remote leg — say so explicitly rather
    // than reporting a generic failure. Typed error, not a message-string match.
    const local = e instanceof LocalCatalogDisabledError;
    if (opts.store) {
      logErr(
        local
          ? "remote catalog disabled (catalog.url: local) — set catalog.url: https://skaile.store to search the store"
          : `Store search failed: ${e.message}`,
      );
    } else {
      // Degrade, but don't go silent — the user must be able to tell "no store
      // match" from "store never queried".
      logInfo(
        local
          ? "remote catalog disabled (catalog.url: local) — showing local results only"
          : "store unavailable — showing local results only (run with --store to see the error)",
      );
    }
  }
}

/**
 * Create the `skaile search` command.
 *
 * Searches across all locally indexed repos (via {@link AssetManager}) and
 * optionally the remote store. Results are grouped by asset kind and printed in
 * a compact table. Pass `--store` to limit to the remote store, `--local` to
 * limit to local repos, or omit both to query both sources. Accepts an optional
 * `kind` positional to filter by asset type and a `query` positional for
 * keyword matching.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-search-command
 */
export function makeSearchCommand(): Command {
  return new Command("search")
    .description("Search across all repos")
    .argument("[kind]", "Filter by asset kind")
    .argument("[query]", "Search query")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--store", "Search only the store")
    .option("--local", "Search only local repos")
    .action(async (kind?: string, query?: string, opts?: any) => {
      await runSearch(kind, query, opts);
    });
}

/**
 * Parse a `--backend a,b` value into validated {@link DriverTarget}s, exiting on
 * an unknown id. Returns `undefined` when the flag is absent (the caller then
 * resolves the full global backend set).
 */
function parseBackendFilter(raw: string | undefined): DriverTarget[] | undefined {
  if (!raw) return undefined;
  const ids = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const out: DriverTarget[] = [];
  for (const id of ids) {
    if (!(SUPPORTED_DRIVER_TARGETS as string[]).includes(id)) {
      logErr(`unknown backend '${id}' — expected one of: ${SUPPORTED_DRIVER_TARGETS.join(", ")}`);
      process.exit(1);
    }
    out.push(id as DriverTarget);
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Soft-dep opt-in pull for `skaile add`, symmetric with the remove-orphan prompt.
 * For each absent body-link soft dep with a parseable ref, prompt (default No) to
 * also add it; on yes, add the target via `am.add`. Report-only otherwise — never
 * auto-adds. A non-interactive run (no TTY) prints the report-only hint instead of
 * prompting, so a piped `skaile add` never blocks. The clack prompt / TTY check /
 * loggers are injected so the decision is unit-testable without a real terminal.
 */
export async function promptSoftDepAdds(
  am: Pick<AssetManager, "reportSoftDeps" | "add">,
  ref: string,
  io: {
    isTTY: boolean;
    link?: boolean;
    /** `--with-soft-deps`: pull every referenced soft dep without prompting. */
    withSoftDeps?: boolean;
    /** `--no-soft-deps`: skip the soft-dep step entirely (no prompt, no hint). */
    noSoftDeps?: boolean;
    confirm: (message: string) => Promise<boolean | symbol>;
    isCancel: (value: unknown) => boolean;
    onAdded: (line: string) => void;
    onError: (line: string) => void;
    onHint: (line: string) => void;
  },
): Promise<void> {
  if (io.noSoftDeps) return; // opted out — don't even query the index
  const edges = await am.reportSoftDeps([ref]);
  if (edges.length === 0) return;

  const addEdge = async (toRef: string): Promise<void> => {
    try {
      for (const a of await am.add(toRef, { link: io.link })) io.onAdded(a);
    } catch (err) {
      io.onError(`Failed to add ${toRef}: ${err}`);
    }
  };

  // `--with-soft-deps`: opt-in one-shot pull of every referenced soft dep,
  // no prompt (TTY-independent, for CI/scripts). One level only — matches
  // reportSoftDeps([ref]); a pulled dep's own soft deps are not followed.
  if (io.withSoftDeps) {
    for (const edge of edges) {
      if (edge.toRef) await addEdge(edge.toRef);
      // Surface the skip — a CI/script run should not silently drop a soft dep.
      else io.onError(`Cannot auto-add ${bareSoftDepName(edge.to)} — no resolvable ref.`);
    }
    return;
  }

  // Display the bare names (grilling), but keep the full toRef in the runnable hint.
  if (!io.isTTY) {
    // No TTY → can't prompt without hanging; fall back to the printed hint.
    for (const edge of edges) io.onHint(formatSoftDepHint(edge));
    return;
  }
  for (const edge of edges) {
    if (!edge.toRef) continue; // no parseable ref → can't be added reliably
    const yes = await io.confirm(
      `${bareSoftDepName(edge.from)} references ${bareSoftDepName(edge.to)} (soft dependency) — also add it?`,
    );
    if (io.isCancel(yes)) break; // Ctrl-C: stop prompting, keep what's added
    if (yes === true) await addEdge(edge.toRef);
  }
}

/**
 * The `remove` counterpart to {@link promptSoftDepAdds}: after removing `ref`,
 * offer to also remove each soft-dep target of `ref` that is now orphaned (no
 * installed asset still references it). Default No — never auto-removed (removing
 * on a heuristic is the dangerous direction). A non-interactive run (no TTY)
 * prints the report-only hint instead of prompting; `--no-soft-deps` skips it.
 */
export async function promptSoftDepRemovals(
  am: Pick<AssetManager, "orphanedSoftDepsAfterRemoving" | "remove">,
  ref: string,
  io: {
    isTTY: boolean;
    /** `--no-soft-deps`: skip the orphan step entirely (no prompt, no hint). */
    noSoftDeps?: boolean;
    confirm: (message: string) => Promise<boolean | symbol>;
    isCancel: (value: unknown) => boolean;
    onRemoved: (line: string) => void;
    onHint: (line: string) => void;
  },
): Promise<void> {
  if (io.noSoftDeps) return;
  // Only orphans with a parseable ref can be removed.
  const orphans = (await am.orphanedSoftDepsAfterRemoving([ref])).filter((o) => o.targetRef);
  if (orphans.length === 0) return;

  if (!io.isTTY) {
    // No TTY → can't prompt without hanging; fall back to the printed hint.
    for (const o of orphans) {
      io.onHint(
        `${bareSoftDepName(o.target)} is now an orphaned soft dependency of ${ref} — run \`skaile remove ${o.targetRef}\` to remove it.`,
      );
    }
    return;
  }
  for (const o of orphans) {
    const yes = await io.confirm(
      `${bareSoftDepName(o.target)} is a soft dependency of ${ref} and is no longer referenced by any installed asset — also remove it?`,
    );
    if (io.isCancel(yes)) break; // Ctrl-C: stop prompting, keep what's already removed
    if (yes === true && am.remove(o.targetRef!)) io.onRemoved(`Removed ${o.targetRef}`);
  }
}

/**
 * Create the `skaile add` command.
 *
 * Installs a skill, agent, flow, or bundle from the catalog into the current
 * workspace. Resolves transitive dependencies declared in the asset's manifest
 * and deploys all files to the driver target directory (default: `claude-code`).
 * Accepts `--global` to deploy to `~/.claude` instead of the project directory.
 * The asset reference format is `kind:@<publisher>/name#<version>` (e.g.
 * `skill:@skaile/use-exa#1.0.0`).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-add-command
 */
export function makeAddCommand(): Command {
  return new Command("add")
    .description("Add to skaile.yaml + deploy")
    .argument("<ref>", "Asset reference (kind:@<publisher>/name#<version>)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--global", "Record in ~/.skaile/skaile.yaml + deploy to every global backend")
    .option(
      "--backend <ids>",
      "Comma-separated global backends to deploy into (default: all resolved); implies --global",
    )
    .option("--target <agent>", "Driver target", "claude-code")
    .option(
      "--link",
      "symlink local-path assets instead of copying (dev loop; machine-local, not for committing)",
    )
    .option(
      "--with-soft-deps",
      "also add the asset's report-only soft (body-link) deps, without prompting",
    )
    .option("--no-soft-deps", "skip the soft-dep prompt entirely (don't add, don't report)")
    .action(async (ref: string, opts) => {
      if (opts.withSoftDeps && opts.softDeps === false) {
        logErr("--with-soft-deps and --no-soft-deps are mutually exclusive.");
        process.exit(1);
      }
      const backends = parseBackendFilter(opts.backend);
      const am = new AssetManager({
        projectDir: path.resolve(opts.projectDir),
        global: opts.global || backends !== undefined,
        backends,
        driverTarget: opts.target,
      });
      const spinner = p.spinner();
      spinner.start(`Adding ${ref}`);
      try {
        const added = await am.add(ref, { link: opts.link });
        spinner.stop("Done");
        // Surface body-link soft deps the asset references; prompt (default No) to
        // optionally pull each one. Non-interactive runs fall back to a hint.
        await promptSoftDepAdds(am, ref, {
          isTTY: !!process.stdin.isTTY,
          link: opts.link,
          withSoftDeps: opts.withSoftDeps,
          noSoftDeps: opts.softDeps === false,
          confirm: (message) => p.confirm({ message, initialValue: false }),
          isCancel: p.isCancel,
          onAdded: logOk,
          onError: logErr,
          onHint: (line) => logInfo(S.dim(line)),
        });
        if (added.length === 0) {
          // add() resolved + recorded the ref (it throws on not-found before
          // here), so an empty result means either a non-deployable kind or an
          // already-deployed asset — not "not found".
          const kind = parseAssetRef(ref).kind;
          if (NON_DEPLOYABLE_KINDS.has(kind))
            logInfo(
              `Recorded in skaile.yaml${kind === "mcp-server" ? " (bytes fetched + verified into .skaile/cache/store)" : ""}. ${kind} assets are not deployed into the driver target — they are wired when skaile runs the agent.`,
            );
          else logInfo("Nothing to deploy (already up to date).");
        } else for (const a of added) logOk(a);
      } catch (err) {
        spinner.stop("Failed");
        logErr(String(err));
        process.exit(1);
      }
    });
}

/**
 * Create the `skaile remove` command.
 *
 * Removes a deployed asset from the workspace by reference (`kind:name`). Cleans
 * up all files tracked in `.skaile/deployed.yaml` for that asset. Accepts
 * `--global` to target the global deployment in `~/.claude` and `--target` to
 * specify the driver target directory.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-remove-command
 */
export function makeRemoveCommand(): Command {
  return new Command("remove")
    .description("Remove from skaile.yaml + undeploy")
    .argument("<ref>", "Asset reference (kind:name)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--global", "Remove from ~/.skaile/skaile.yaml + undeploy from every global backend")
    .option(
      "--backend <ids>",
      "Comma-separated global backends to undeploy from (default: all resolved); implies --global",
    )
    .option("--target <agent>", "Driver target", "claude-code")
    .option("--no-soft-deps", "skip the orphaned-soft-dep prompt (don't remove, don't report)")
    .action(async (ref: string, opts) => {
      const backends = parseBackendFilter(opts.backend);
      const am = new AssetManager({
        projectDir: path.resolve(opts.projectDir),
        global: opts.global || backends !== undefined,
        backends,
        driverTarget: opts.target,
      });
      const ok = am.remove(ref);
      if (!ok) {
        logErr(`Not deployed: ${ref}`);
        return;
      }
      logOk(`Removed ${ref}`);
      // Report-only: offer to also remove soft-dep targets of `ref` now orphaned.
      // Default No — never auto-removed (removing on a heuristic is the dangerous
      // direction, so there is deliberately no batch `--with-soft-deps` here).
      await promptSoftDepRemovals(am, ref, {
        isTTY: !!process.stdin.isTTY,
        noSoftDeps: opts.softDeps === false,
        confirm: (message) => p.confirm({ message, initialValue: false }),
        isCancel: p.isCancel,
        onRemoved: logOk,
        onHint: (line) => logInfo(S.dim(line)),
      });
    });
}

/** Colour a declared-dep state for the `list` table. */
function stateLabel(s: DeclaredState): string {
  if (s === "installed") return S.ok(s);
  if (s === "drift") return S.warn(s);
  if (s === "invalid") return S.err(s);
  return S.dim(s);
}

/**
 * Create the `skaile list` command.
 *
 * Lists the **declared** top-level dependencies from `skaile.yaml` (not the
 * deployed tree), each annotated with a reconciliation state — `declared`,
 * `installed`, or `drift`. Transitive (lock-only) deps are excluded. The
 * available-assets view lives in `skaile search`. Delegates to
 * {@link AssetManager.listDeclared}.
 *
 * `--target` selects which driver-target dir is inspected for the on-disk
 * install/drift check. `--global` switches to the global SSOT
 * (`~/.skaile/skaile.yaml`) and reports state **per backend**; `--backend <ids>`
 * narrows the global view to a subset.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-list-command
 */
export function makeListCommand(): Command {
  return new Command("list")
    .description("List declared dependencies from skaile.yaml with state")
    .argument("[kind]", "Filter by asset kind")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option(
      "-g, --global",
      "Read the global SSOT (~/.skaile/skaile.yaml); report state per backend",
    )
    .option("--backend <ids>", "Comma-separated global backends to report (implies --global)")
    .option("--json", "Output as JSON")
    .addOption(
      new Option("--target <agent>", "Driver target")
        .default("claude-code")
        .choices(SUPPORTED_DRIVER_TARGETS),
    )
    .action(async (kind: string | undefined, opts) => {
      const backends = parseBackendFilter(opts.backend);
      if (opts.global || backends) {
        runGlobalList(kind, opts, backends);
        return;
      }
      const am = new AssetManager({
        projectDir: path.resolve(opts.projectDir),
        driverTarget: opts.target,
      });
      let declared: DeclaredAsset[];
      try {
        declared = await am.listDeclared(kind);
      } catch (err) {
        if (err instanceof MissingManifestError)
          logErr(`No ${S.cmd("skaile.yaml")} found. Run ${S.cmd("skaile init")} to create one.`);
        else logErr(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }

      if (opts.json) {
        console.log(JSON.stringify(declared, null, 2));
        return;
      }
      if (declared.length === 0) {
        logInfo("No dependencies declared in skaile.yaml.");
        return;
      }
      const kindW = Math.max(4, ...declared.map((e) => e.kind.length)); // "KIND" = 4
      const nameW = Math.max(4, ...declared.map((e) => e.name.length)); // "NAME" = 4
      console.log();
      console.log(
        `  ${S.dim("KIND".padEnd(kindW))}  ${S.dim("NAME".padEnd(nameW))}  ${S.dim("STATE")}`,
      );
      // Rule spans both columns + the two 2-space gaps + the widest STATE label.
      console.log(`  ${S.rule(kindW + nameW + 4 + "installed".length)}`);
      for (const e of declared) {
        console.log(
          `  ${kindColorPad(e.kind, kindW)}  ${e.name.padEnd(nameW)}  ${stateLabel(e.state)}`,
        );
      }
      console.log(`\n  ${S.dim(`${declared.length} declared`)}`);
      const drift = declared.filter((e) => e.state === "drift").length;
      if (drift > 0)
        logInfo(`${drift} with drift — run ${S.cmd("skaile install")} to materialize.`);
      const invalid = declared.filter((e) => e.state === "invalid").length;
      if (invalid > 0)
        logInfo(`${invalid} invalid — fix the ${S.cmd("skaile.yaml")} dependencies entry.`);
      console.log();
    });
}

/** Options accepted by the global branch of `skaile list`. */
interface GlobalListOptions {
  projectDir: string;
  json?: boolean;
}

/**
 * `skaile list --global` branch: per-backend reconciliation of the global SSOT.
 * One row per (ref, backend); `--json` emits `{ ref, kind, name, backend, state }`.
 */
function runGlobalList(
  kind: string | undefined,
  opts: GlobalListOptions,
  backends: DriverTarget[] | undefined,
): void {
  const am = new AssetManager({
    projectDir: path.resolve(opts.projectDir),
    global: true,
    backends,
  });
  let rows: BackendDeclaredAsset[];
  try {
    rows = am.globalState({ kind });
  } catch (err) {
    logErr(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }

  if (opts.json) {
    console.log(JSON.stringify(rows, null, 2));
    return;
  }
  if (rows.length === 0) {
    logInfo("No global dependencies declared in ~/.skaile/skaile.yaml.");
    return;
  }
  const kindW = Math.max(4, ...rows.map((e) => e.kind.length));
  const nameW = Math.max(4, ...rows.map((e) => e.name.length));
  const beW = Math.max(7, ...rows.map((e) => e.backend.length)); // "BACKEND" = 7
  console.log();
  console.log(
    `  ${S.dim("KIND".padEnd(kindW))}  ${S.dim("NAME".padEnd(nameW))}  ${S.dim("BACKEND".padEnd(beW))}  ${S.dim("STATE")}`,
  );
  console.log(`  ${S.rule(kindW + nameW + beW + 6 + "installed".length)}`);
  for (const e of rows) {
    console.log(
      `  ${kindColorPad(e.kind, kindW)}  ${e.name.padEnd(nameW)}  ${e.backend.padEnd(beW)}  ${stateLabel(e.state)}`,
    );
  }
  console.log(`\n  ${S.dim(`${rows.length} entries`)}`);
  const drift = rows.filter((e) => e.state === "drift").length;
  if (drift > 0)
    logInfo(`${drift} with drift — run ${S.cmd("skaile install --global")} to materialize.`);
  console.log();
}

/** Store leg of the `skaile info` lookup; injected in tests to stay network-free. */
export type InfoStoreLookup = (filter: { kind?: string }) => Promise<CatalogAsset[]>;

/** The slice of `AssetManager` the info lookup needs (kept narrow for tests). */
interface InfoManagerLike {
  info(ref: string): Promise<CatalogEntry | null>;
  listDeployed(kind?: string): CatalogEntry[];
}

export interface InfoLookupResult {
  entry: CatalogEntry | null;
  /** Why the store leg produced nothing — folded into the not-found hint. */
  storeNote?: string;
  /**
   * The store leg was off by configuration (`catalog.url: local`), not down.
   * Keeps the hint from telling the user to retry something that will never
   * succeed until they change their config.
   */
  storeDisabled?: boolean;
}

/**
 * Map a remote catalog asset to the local entry shape `info` prints. Mirrors
 * `asset-feeds.ts` `storeAssets()`: remote assets have no per-file path, so
 * `source` is the stable `store://<id>` placeholder.
 */
function catalogAssetToEntry(a: CatalogAsset): CatalogEntry {
  return {
    name: a.name,
    kind: (a.kind as CatalogEntry["kind"]) ?? "skill",
    description: a.description ?? "",
    source: `store://${a.id}`,
    // Bare, like every other leg — a stored `@publisher` must not render as
    // `repository: @admin` for a store hit and `admin` for a local one.
    publisher: a.publisher.replace(/^@/, ""),
    repo: a.repo,
    domainSlug: a.domainSlug,
    category: a.category,
    version: a.version,
    requires: [],
    dependencies: [],
  };
}

/**
 * Resolve `skaile info <ref>` across every place the asset can live, cheapest
 * leg first:
 *
 * 1. **Local sources** — `AssetManager.info` (source clones + the bundled
 *    factory tree).
 * 2. **Deployed assets** — this project's deploy dirs, re-scanned from disk so
 *    an installed store asset resolves with real metadata and no network.
 * 3. **Catalog source** — the configured store, mapped through
 *    {@link catalogAssetToEntry}.
 *
 * A failing store leg (unreachable host, `catalog.url: local` with no local
 * library, auth failure) never throws — it comes back as `storeNote` so the
 * caller can print an accurate hint.
 *
 * @docLink cli/commands/catalog#make-info-command
 */
export async function resolveInfoEntry(
  am: InfoManagerLike,
  ref: string,
  opts: { projectDir: string; storeLookup?: InfoStoreLookup },
): Promise<InfoLookupResult> {
  const local = await am.info(ref);
  if (local) return { entry: local };

  const parsed = parseRefTolerant(ref);
  if (!parsed) return { entry: null };

  // Deployed leg — offline. `listDeployed` entries are shallow (no description
  // or version), so re-read the real manifest from the deployed directory.
  const deployed = am
    .listDeployed(parsed.kind)
    .find((e) => e.name === parsed.name && e.kind === parsed.kind);
  if (deployed) {
    // `scanDirectory` stamps its `repoName` onto every entry as the publisher,
    // and a deployed asset has none recorded — pass "" so `info` omits the
    // `repository:` line instead of fabricating one from the ref.
    const scanned = await scanDirectory(deployed.source, deployed.publisher ?? "");
    const match =
      scanned.find((e) => e.kind === deployed.kind && e.name === deployed.name) ??
      // A manifest whose `name:` differs from the deploy dir still matches on
      // kind. Never fall back across kinds — a nested manifest of another kind
      // is a different asset, not this one.
      scanned.find((e) => e.kind === deployed.kind);
    // A single-file deploy path scans to nothing — fall back to the shallow entry.
    return { entry: match ?? deployed };
  }

  const lookup: InfoStoreLookup =
    opts.storeLookup ??
    (async (filter) => {
      const { resolveCatalogSource } = await import("../open-library.ts");
      const { source, close } = await resolveCatalogSource({ projectDir: opts.projectDir });
      try {
        return await source.listAssets(filter);
      } finally {
        close();
      }
    });
  try {
    const assets = await lookup({ kind: parsed.kind });
    // Stored publishers may carry the `@` scope sigil that `parseAssetRef`
    // strips — compare both sides bare (as the catalog sources themselves do).
    const wanted = parsed.publisher?.replace(/^@/, "");
    const hit = assets.find(
      (a) =>
        a.kind === parsed.kind &&
        a.name === parsed.name &&
        (!wanted || a.publisher.replace(/^@/, "") === wanted),
    );
    return { entry: hit ? catalogAssetToEntry(hit) : null };
  } catch (err) {
    const { LocalCatalogDisabledError } = await import("../open-library.ts");
    const storeNote = err instanceof Error ? err.message : String(err);
    // Typed error for `catalog.url: local`; the local dispatch also throws a
    // plain Error (no library registered / path gone) naming the same key.
    const disabled = err instanceof LocalCatalogDisabledError || /catalog\.url/i.test(storeNote);
    return { entry: null, storeNote, ...(disabled ? { storeDisabled: true } : {}) };
  }
}

/**
 * Not-found hint for `skaile info`, naming the legs that were actually searched.
 * A `local`-sentinel catalog is a choice, not an outage, so it gets a config
 * hint rather than "retry when the store is reachable".
 */
export function infoNotFoundHint(
  ref: string,
  lookup: Pick<InfoLookupResult, "storeNote" | "storeDisabled">,
): string {
  const sync = `Run ${S.cmd("skaile source sync")} for source-backed assets`;
  if (!lookup.storeNote)
    return `'${ref}' not found in local sources, deployed assets, or the store. ${sync}, or ${S.cmd("skaile search --store")} to browse the store.`;
  if (lookup.storeDisabled)
    return `'${ref}' not found in local sources or deployed assets, and the remote store is disabled (${lookup.storeNote}). ${sync}, or set ${S.cmd("catalog.url")} in ~/.skaile/config.yaml to query the store.`;
  return `'${ref}' not found locally and the store could not be queried (${lookup.storeNote}). ${sync}, or retry when the store is reachable.`;
}

/**
 * Create the `skaile info` command.
 *
 * Shows metadata for a catalog entry identified by `kind:name`: description,
 * version, source repository, required platform resources, and transitive skill
 * dependencies. The ref is resolved via {@link resolveInfoEntry} — local
 * sources, then this project's deployed assets, then the configured store — and
 * exits with an error (and a leg-accurate hint) when nothing matches.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-info-command
 */
export function makeInfoCommand(): Command {
  return new Command("info")
    .description("Show asset metadata, deps, deploy status")
    .argument("<ref>", "Asset reference (kind:name)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (ref: string, opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const am = new AssetManager({ projectDir });
      const lookup = await resolveInfoEntry(am, ref, { projectDir });
      const entry = lookup.entry;
      if (!entry) {
        logErr(infoNotFoundHint(ref, lookup));
        process.exit(1);
      }
      // Inferred body-link soft deps (report-only) — read from the index, distinct
      // from the typed `requires` above.
      const softDeps = await am.softDepsOf([ref]);
      console.log();
      console.log(`  ${S.heading(entry.name)}`);
      console.log(`  ${S.rule(40)}`);
      console.log(`  kind:        ${kindColor(entry.kind)}`);
      if (entry.version) console.log(`  version:     ${entry.version}`);
      if (entry.description) console.log(`  description: ${entry.description}`);
      if (entry.publisher) console.log(`  repository:  ${S.dim(entry.publisher)}`);
      console.log(`  source:      ${S.dim(entry.source)}`);
      if (entry.requires.length) {
        console.log(`  requires:`);
        for (const d of entry.requires)
          console.log(`    ${S.dim("·")}  ${kindColor(d.kind)}:${d.name}`);
      }
      if (entry.dependencies.length) {
        console.log(`  dependencies:`);
        for (const s of entry.dependencies) console.log(`    ${S.dim("·")}  ${colorRef(s)}`);
      }
      for (const line of formatSoftDepSection(softDeps.map((e) => bareSoftDepName(e.to))))
        console.log(line);
      console.log();
    });
}

/**
 * Create the `skaile create` command.
 *
 * Scaffolds a new skill, agent, flow, or bundle in the target directory using
 * the AI skill builder via {@link AssetManager.create}. Accepts `-k`/`--kind`
 * to specify the asset type (default: `skill`) and `-d`/`--dir` to set the
 * destination directory (default: current working directory).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-create-command
 */
export function makeCreateCommand(): Command {
  return new Command("create")
    .description("Scaffold a new asset")
    .argument("<name>", "Asset name")
    .addOption(
      new Option("-k, --kind <kind>", "Asset kind").default("skill").choices([...ASSET_KINDS]),
    )
    .option("-d, --dir <dir>", "Target directory (default: cwd)")
    .action((name: string, opts) => {
      const am = new AssetManager({ projectDir: process.cwd() });
      const destDir = opts.dir ? path.resolve(opts.dir) : process.cwd();
      const result = am.create(name, opts.kind, destDir);
      if (result.ok) logOk(`Created ${kindColor(opts.kind)}: ${pc.dim(result.path)}`);
      else {
        logErr(result.path);
        process.exit(1);
      }
    });
}

/**
 * Create the `skaile doctor` command.
 *
 * Scans all deployed assets for missing transitive dependency chains. For each
 * gap it reports the affected asset, the missing dependency, and whether the dep
 * is available in a known repo (fixable) or completely absent. Delegates to
 * {@link AssetManager.doctor}.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/catalog#make-doctor-command
 */
export function makeDoctorCommand(): Command {
  return new Command("doctor")
    .description("Scan for missing dependency chains")
    .argument("[name]", "Check a specific asset (kind:name)")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (name: string | undefined, opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      const issues = await am.doctor();
      if (issues.length === 0) {
        logOk("No missing dependencies.");
        return;
      }
      const assetW = Math.max(5, ...issues.map((i) => `${i.assetKind}:${i.assetName}`.length));
      const depW = Math.max(11, ...issues.map((i) => `${i.depKind}:${i.depName}`.length));
      console.log();
      console.log(
        `  ${S.dim("ASSET".padEnd(assetW))}  ${S.dim("MISSING DEP".padEnd(depW))}  ${S.dim("IN REPOS")}`,
      );
      console.log(`  ${S.rule(assetW + depW + 15)}`);
      for (const i of issues) {
        const asset = `${i.assetKind}:${i.assetName}`;
        const dep = `${i.depKind}:${i.depName}`;
        const status = i.inRepos ? S.warn("fixable") : S.err("not found");
        console.log(`  ${asset.padEnd(assetW)}  ${dep.padEnd(depW)}  ${status}`);
      }
      console.log();
    });
}
